// =============================================================================
// io/sync/patterns.ts — the user's pattern catalogue.
// -----------------------------------------------------------------------------
// Small, user-scoped, and read by three things that have all been blocked on it:
// the annotation editor's pattern picker, the Setup Quality analytics sub-tab, and
// the global filter's Pattern facet.
//
// ── ⚠ WHY A FAILED READ THROWS ──
//
// `trades.pattern_id` is a FOREIGN KEY to `patterns.id`, and the filter drawer
// PRUNES a selected pattern id that no longer resolves — the old client does this
// deliberately, "so a dangling id can't silently filter every trade out". With an
// empty catalogue that pruning would silently clear an active filter, and the user
// would see an unfiltered book while the chip bar showed nothing. An empty result
// and a failed read must not be the same value.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import {
  patternVersions as versionsOf, type Criterion, type VersionedPattern,
} from '../../domain/patternVersions';
import { PAGE_ROWS } from './load';

/**
 * Only the fields something actually reads today.
 *
 * ⚠ Deliberately NOT the whole row. The context arrays (`sessions`, `regimes`,
 * `days_of_week`) and the execution rules drive the pattern EDITOR, which is not
 * ported — selecting them now would be a column list nobody reads, drifting until
 * someone needs it.
 */
export interface PatternSummary {
  id: string;
  name: string;
  category: string;
  description: string;
  /** ⚠ The LEGACY free-text rules block — not the itemised `entryCriteria`. */
  criteria: string;
  /**
   * ⚠ THE SECOND HALF OF THE LEGACY BLOCK, and it is a real column.
   *
   * The old client's editor renders `criteria` AND `entry_trigger` together under
   * "Legacy rules text — from before criteria were itemized", and its Clear button
   * wipes BOTH. Selecting only `criteria` would give this client a Clear button
   * that half-works: the user is told the prose is gone, and a paragraph stays on
   * the row where nothing will ever show it to them again.
   *
   * ⚠ `stop_rule` / `target_rule` / `scale_out_rules` are also still columns on
   * this table and are deliberately NOT read — the old client retired them onto
   * Exit Models and its editor no longer renders them either. They are also never
   * written here, so whatever they hold survives untouched.
   */
  entryTrigger: string;
  invalidation: string;
  sessions: string[];
  regimes: string[];
  /** ⚠ NUMBERS: 0 = Sunday … 6 = Saturday. The column is INTEGER[]. */
  daysOfWeek: number[];
  priorDayRel: string;
  news: string;
  screenshots: string[];
  /** ⚠ Unparsed — `domain/patternVersions.ts` owns these shapes. */
  entryCriteria: unknown;
  versions: unknown;
  createdAt: unknown;
  candidateVersion: number | null;
  /** The whole `extras` blob, so a save can merge rather than rebuild. */
  extras: Record<string, unknown>;
  /**
   * ⚠ `'candidate'` means NOT TAGGABLE on a live trade. The old client's
   * `livePatterns()` filters these out of every trade-form picker, and the chip
   * beside them says why: "not taggable on live trades until you promote it".
   *
   * A picker that offered them would let a trader tag real money against rules
   * that are explicitly still being tested — and the pattern's own analytics
   * would then mix tested and untested trades under one name.
   */
  status: string;
  /**
   * The rule version to PIN when this pattern is newly tagged onto a trade.
   *
   * ⚠ Rides `extras`, like `status` — neither is a column. The pin freezes which
   * version's entry criteria a trade is judged against, so a later rule edit does
   * not retroactively rewrite what a past trade was supposed to satisfy.
   */
  liveVersion: number | null;
}

/**
 * ⚠ `extras` is in the projection because the LIFECYCLE is not columns — the
 * patterns table predates it, so `status`, `liveVersion`, `candidateVersion`,
 * `versions[]` and `entryCriteria` all ride the forward-compat JSONB. Selecting
 * `id,name,category` alone returns a catalogue that cannot tell a promoted
 * pattern from a candidate, let alone which version a trade was judged by.
 *
 * ⚠ `days_of_week` is INTEGER[] — see `Pattern.daysOfWeek`. It was typed
 * `string[]` in the domain model and that was wrong against the real row.
 */
export const PATTERNS_SELECT =
  'id,name,category,description,criteria,entry_trigger,sessions,regimes,days_of_week,'
  + 'prior_day_rel,news,invalidation,screenshots,extras';

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const strArr = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

export function patternFromDb(r: Record<string, unknown>): PatternSummary {
  const extras = (r['extras'] && typeof r['extras'] === 'object')
    ? (r['extras'] as Record<string, unknown>) : {};
  const lv = Number(extras['liveVersion']);
  const cv = Number(extras['candidateVersion']);
  return {
    id: String(r['id'] ?? ''),
    // ⚠ A pattern with no name still has to be pickable and labelled, or a trade
    // annotated with it renders blank in every list.
    name: String(r['name'] ?? '') || '(untitled)',
    category: str(r['category']),
    description: str(r['description']),
    // ⚠ `criteria` is the LEGACY free-text rules block, shown collapsed in the
    // editor as "from before criteria were itemized". It is NOT `entryCriteria`,
    // which is the itemised checklist in `extras`. Two different fields, similar
    // names, and only one of them drives the trade form.
    criteria: str(r['criteria']),
    entryTrigger: str(r['entry_trigger']),
    invalidation: str(r['invalidation']),
    sessions: strArr(r['sessions']),
    regimes: strArr(r['regimes']),
    // ⚠ NUMBERS. 0 = Sunday … 6 = Saturday.
    daysOfWeek: Array.isArray(r['days_of_week'])
      ? (r['days_of_week'] as unknown[]).map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6)
      : [],
    priorDayRel: str(r['prior_day_rel']),
    news: str(r['news']),
    screenshots: strArr(r['screenshots']),
    status: str(extras['status']),
    liveVersion: Number.isFinite(lv) && lv > 0 ? lv : null,
    candidateVersion: Number.isFinite(cv) && cv > 0 ? cv : null,
    // ⚠ Passed through UNPARSED. `domain/patternVersions.ts` owns their shape and
    // normalises on the way in — parsing here too would be two places to keep in
    // step, and the version list is the one structure that must not drift.
    entryCriteria: extras['entryCriteria'],
    versions: extras['versions'],
    createdAt: extras['createdAt'],
    /**
     * ⚠ The WHOLE extras blob, kept so the writer can merge over it. The
     * lifecycle is only part of what lives here, and a save that rebuilt extras
     * from the keys this client knows would drop the rest — the same failure
     * `contractToBlob` was built to prevent on the journal side.
     */
    extras,
  };
}

/** The itemised entry criteria, normalised out of the unparsed `extras` value. */
export function patternCriteria(p: PatternSummary | null | undefined): Criterion[] {
  const v = p?.entryCriteria;
  if (!Array.isArray(v)) return [];
  return (v as unknown[]).flatMap((c) => {
    if (!c || typeof c !== 'object') return [];
    const r = c as Record<string, unknown>;
    const id = typeof r['id'] === 'string' ? r['id'] : '';
    return id ? [{ id, label: typeof r['label'] === 'string' ? r['label'] : '' }] : [];
  });
}

/**
 * A loaded pattern, in the shape the lifecycle functions take.
 *
 * ⚠ ONE ADAPTER, not one per caller. The editor's version panel, "▲ Promote to
 * production" on a card, and both version buttons all need this same mapping, and
 * three hand-written copies is three chances to pass `liveVersion` as `null` — at
 * which point `reconcileVersions` compares the working rules against `undefined`,
 * decides nothing changed, and silently declines to spin a candidate. That failure
 * writes a clean row and shows no error.
 *
 * ⚠ `versionsOf` is called here so the list is ALWAYS non-empty: a pattern that
 * predates the lifecycle gets a synthesised live v1 from its current rules, and no
 * caller needs an "unversioned pattern" branch.
 */
export function patternLifecycle(p: PatternSummary): VersionedPattern {
  const rules = {
    entryCriteria: patternCriteria(p),
    invalidation: p.invalidation,
  };
  return {
    // ⚠ Defaults to `production`, matching the writer. An empty status on a row
    // that predates the lifecycle must not read as `candidate` — that would make
    // an established pattern un-taggable and offer to "promote" it.
    status: p.status || 'production',
    versions: versionsOf({
      ...rules,
      versions: p.versions as never,
      // ⚠ `createdAt` rides `extras` and is passed through UNPARSED, so it is
      // `unknown` here. Narrowed rather than cast: a synthesised v1 stamps this
      // value, and an object landing in a timestamp field would render as
      // "[object Object]" in the version panel and sort unpredictably.
      createdAt: (typeof p.createdAt === 'string' || typeof p.createdAt === 'number')
        ? p.createdAt : 0,
    }),
    liveVersion: p.liveVersion ?? 1,
    candidateVersion: p.candidateVersion,
    ...rules,
  };
}

/**
 * The patterns a live trade may be tagged with — the old client's `livePatterns()`.
 *
 * ⚠ Filtering happens at the PICKER, never at the read. A trade already tagged
 * with a pattern that has since become a candidate must still resolve its name
 * everywhere it is displayed; dropping candidates from the catalogue would make
 * that trade render as untagged and invite a save that clears the link.
 */
export function taggablePatterns(
  patterns: readonly PatternSummary[] | null,
): readonly PatternSummary[] {
  return (patterns ?? []).filter((p) => p.status !== 'candidate');
}

export async function loadPatterns(sb: SupabaseClient): Promise<PatternSummary[]> {
  const { data, error } = await sb
    .from('patterns')
    .select(PATTERNS_SELECT)
    .order('name', { ascending: true });

  if (error) {
    throw new Error(
      `loadPatterns: ${error.message}. Refusing to continue with an empty catalogue — ` +
        `an active Pattern filter would be pruned as a dangling id and silently cleared, ` +
        `showing an unfiltered book with nothing in the filter bar to explain it.`,
    );
  }

  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  if (rows.length >= PAGE_ROWS) {
    throw new Error(
      `loadPatterns: got ${rows.length} rows, at or above the ${PAGE_ROWS}-row server cap. ` +
        `This read is unpaged because a pattern catalogue is hand-curated; at this size it ` +
        `is being truncated and some patterns would be unpickable.`,
    );
  }

  return rows.map(patternFromDb);
}
